import { useCallback, useEffect, useMemo, useState } from 'react';
import { isSameDay, isSameMonth, isSameYear } from 'date-fns';
import {
  AppBarDataCustomItem,
  AppBarDataDatePicker,
  AppBarDataDropdown,
  TimeseriesChartItem,
} from '@ihsm/ui-shared';
import { detailsChartFormattedData } from '@ihsm/ui-shared/lib/esm/shared/components/DetailedTimeseriesChart/utils';
import {
  useGetAssetClassesQuery,
  useGetAssetClassFactorsQuery,
  useGetAssetFactorsTimeSeriesQuery,
} from 'services/newApi/marketDataApi';
import { useActions, useAppSelector } from 'state/store';
import { TIME_SERIES, TABLE } from 'shared/constants/viewOptions';
import { sortByPeriodString } from 'shared/utils/sortByPeriodString';
import { getMarketDataTableData } from '../components/MarketDataTable/utils';
import { DEFAULT_DATA } from '../constants';

export type UseMarketDataPage = {
  forceLoadingScreen: boolean;
  forceNoDataScreen: boolean;
  selectedAssetClassesFactor: string;
  onAssetClassChange: (value: string) => void;
  assetClasses?: string[];
  factors: string[];
  assetClassType: string;
  onChartChange: (value: string) => void;
  selectedViewOptions: string[];
  setSelectedViewOptions: (values: string[]) => void;
  isTimeseriesVisible: boolean;
  isTableVisible: boolean;
  chartData: TimeseriesChartItem[];
  isSearchTreeCollapsed: boolean;
  isSideBarCollapsed: boolean;
  appBarMenuItems: (
    | AppBarDataDatePicker
    | AppBarDataDropdown
    | AppBarDataCustomItem
  )[];
};

export const useMarketDataPage = (): UseMarketDataPage => {
  const { setSelectedAssetClass, setSelectedFactor } = useActions();
  const {
    asOfDate,
    selectedAssetClassesFactor,
    selectedAssetClass,
    assetClassType,
  } = useAppSelector((state) => state.marketDataState);
  const [forceLoadingScreen, setForceLoadingScreen] = useState<boolean>(false);
  const [forceNoDataScreen, setForceNoDataScreen] = useState<boolean>(false);
  const [isAssetClassChanged, setIsAssetClassChanged] =
    useState<boolean>(false);
  const { setAssetClassType } = useActions();

  const { selectedValuationDate: valuationDate, valuationDates } =
    useAppSelector((state) => state.valuationDatesState);

  const { isSearchTreeCollapsed, isSideBarCollapsed } = useAppSelector(
    (state) => state.uiState,
    (prev, next) =>
      prev.isSearchTreeCollapsed === next.isSearchTreeCollapsed &&
      prev.isSideBarCollapsed === next.isSideBarCollapsed
  );
  const { setSelectedValuationDate } = useActions();

  const [selectedViewOptions, setSelectedViewOptions] = useState([
    TIME_SERIES,
    TABLE,
  ]);

  const {
    data: assetClasses = [],
    isLoading: assetClassesLoading,
    isError: assetClassError,
  } = useGetAssetClassesQuery({ asOfDate });

  const {
    data: factors = [],
    isLoading: isFactorsLoading,
    isFetching: isFactorsFetching,
    isError: isFactorError,
    isSuccess: isFactorSuccess,
  } = useGetAssetClassFactorsQuery(
    { asOfDate, selectedAssetClass },
    { skip: !selectedAssetClass }
  );

  const onAssetClassChange = useCallback(
    (value: string) => {
      setForceLoadingScreen(true);
      setIsAssetClassChanged(true);
      setSelectedFactor('');
      setSelectedAssetClass(value);
    },
    [setSelectedAssetClass, setSelectedFactor]
  );

  const {
    data: assetFactorsTimeseries = DEFAULT_DATA,
    isLoading: isFactorTimeseriesLoading,
    isFetching: isFactorTimeseriesFetching,
    isError: isFactorTimeseriesError,
    isSuccess: isFactorTimeseriesSuccess,
  } = useGetAssetFactorsTimeSeriesQuery(
    { valuationDate, selectedAssetClass, selectedAssetClassesFactor },
    { skip: !selectedAssetClassesFactor }
  );

  const chartData = useMemo(
    () => detailsChartFormattedData(assetFactorsTimeseries.timeseries),
    [assetFactorsTimeseries]
  ).sort(sortByPeriodString);

  const gridData = useMemo(() => {
    return getMarketDataTableData(chartData);
  }, [chartData]);

  const onChartChange = useCallback(
    (value: string) => setAssetClassType(value),
    [setAssetClassType]
  );

  const isTimeseriesVisible = selectedViewOptions.includes(TIME_SERIES);
  const isTableVisible = selectedViewOptions.includes(TABLE);

  const dateChangeHandler = useCallback(
    (date: Date | Date[]) => {
      if (
        !Array.isArray(date) &&
        date.getTime() !== new Date(valuationDate)?.getTime()
      ) {
        setSelectedValuationDate(date.toString());
      }
    },
    [setSelectedValuationDate, valuationDate]
  );

  const valuationDatesList = useMemo(() => {
    if (valuationDates) {
      return valuationDates?.map((date) => new Date(date));
    }
    return [];
  }, [valuationDates]);

  const appBarValuationDate: AppBarDataDatePicker = {
    type: 'datepicker',
    labelText: 'Date',
    value: new Date(valuationDate),
    onChange: dateChangeHandler,
    tileDisabled: ({ date }) =>
      valuationDatesList.some(
        (valDate) =>
          isSameDay(valDate, date) &&
          isSameMonth(valDate, date) &&
          isSameYear(valDate, date)
      ),
  };

  const appBarLoadedDate: AppBarDataDatePicker = {
    type: 'datepicker',
    labelText: 'Loaded',
    value: new Date('03-Sep-2021'),
    disabled: true,
  };

  const appBarMenuItems = [appBarValuationDate, appBarLoadedDate];

  useEffect(() => {
    if (
      (chartData?.length && gridData?.length > 1) ||
      isFactorTimeseriesError
    ) {
      setForceLoadingScreen(false);
    }
  }, [chartData, gridData, isFactorTimeseriesError]);

  useEffect(() => {
    if (isFactorTimeseriesLoading || isFactorTimeseriesFetching) {
      setForceLoadingScreen(true);
    }
  }, [isFactorTimeseriesFetching, isFactorTimeseriesLoading]);

  useEffect(() => {
    if (isFactorTimeseriesSuccess && !assetFactorsTimeseries) {
      setForceNoDataScreen(true);
    }
  }, [assetFactorsTimeseries, isFactorTimeseriesSuccess]);

  useEffect(() => {
    setForceLoadingScreen(true);
    if (!isFactorsLoading && !assetClassesLoading) {
      setForceLoadingScreen(false);
    }
  }, [assetClassesLoading, isFactorsLoading]);

  useEffect(() => {
    if (isAssetClassChanged && isFactorsFetching) {
      setForceLoadingScreen(isFactorsFetching);
    }
  }, [assetClasses, isFactorsFetching, isAssetClassChanged]);

  useEffect(() => {
    if (isAssetClassChanged && isFactorSuccess) {
      setForceLoadingScreen(false);
      setIsAssetClassChanged(false);
    }
  }, [isAssetClassChanged, isFactorSuccess]);

  useEffect(() => {
    if (assetClassError || isFactorError) {
      setForceNoDataScreen(true);
    }
  }, [assetClassError, isFactorError]);

  useEffect(() => {
    if (isFactorSuccess && (!factors || !factors?.length)) {
      setForceNoDataScreen(true);
    }
  }, [factors, isFactorSuccess]);

  return {
    forceLoadingScreen,
    forceNoDataScreen,
    selectedAssetClassesFactor,
    onAssetClassChange,
    assetClasses,
    factors,
    assetClassType,
    onChartChange,
    selectedViewOptions,
    setSelectedViewOptions,
    isTimeseriesVisible,
    isTableVisible,
    chartData,
    isSearchTreeCollapsed,
    isSideBarCollapsed,
    appBarMenuItems,
  };
};
